// src/pages/FundPlanner/lib/slgsNoteSchedule.js
import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";

/**
 * buildSlgsNoteCashflows
 * -----------------------
 * Generates semiannual coupon schedule for SLGS Notes.
 *
 * • Day count: ACT/ACT-UST (per 31 CFR 344.5(b))
 * • Coupons occur every 6 months on the maturity's day-of-month.
 * • First coupon = first grid date strictly after settlement.
 * • If <30 days after settlement → mandatory skip (Treasury rule).
 * • If allowSkipFirstCoupon=true → also build an optional-skip variant
 *   that defers the first coupon to the next grid date.
 *
 * Notes:
 * • Interest accrues simply (non-compounded) from settlement to maturity.
 * • Total interest over the life of the Note is identical with or without
 *   a skip; differences are purely in payment timing.
 * • Final flow always includes principal redemption and carries
 *   includesPrincipal = true.
 * • firstInterestPaymentDate is the first non-principal coupon date in the
 *   regular schedule. This is used for SLGS subscription entry.
 */

function getFirstInterestPaymentDate(flows = []) {
  const firstInterestFlow = flows.find((cf) => !cf.includesPrincipal);
  return firstInterestFlow?.date || null;
}

export function buildSlgsNoteCashflows({
  settlement,
  maturity,
  principal,
  ratePct,
  descriptionBase,
  allowSkipFirstCoupon = false,
}) {
  const setl = dayjs(settlement);
  const mat = dayjs(maturity);

  // 1️⃣ Build coupon grid anchored to maturity's day-of-month
  const grid = [];
  let d = mat;
  while (d.isAfter(setl.subtract(3, "year"))) {
    grid.push(d);
    d = d.subtract(6, "month");
  }
  grid.sort((a, b) => a.valueOf() - b.valueOf());

  // first coupon strictly after settlement
  const iNext = grid.findIndex((g) => g.isAfter(setl));
  if (iNext < 0) {
    return {
      regular: [],
      optionalSkip: [],
      firstInterestPaymentDate: null,
      optionalSkipFirstInterestPaymentDate: null,
    };
  }

  const next = grid[iNext];
  const tooClose = next.diff(setl, "day") < 30;

  // ────────────────────────────────────────────────
  // Internal generator for coupons + maturity
  // ────────────────────────────────────────────────
  const makeSchedule = (startIndex) => {
    const flows = [];
    let from = setl;

    for (let i = startIndex; i < grid.length; i++) {
      const to = grid[i];
      const accrual = dayCountFraction(from, to, "act/act-ust");
      const interestAmt = principal * (ratePct / 100) * accrual;
      const isMaturity = to.isSame(mat, "day");

      flows.push({
        date: to.format("YYYY-MM-DD"),
        type: "Inflow",
        desc: isMaturity
          ? `SLGS Note maturity — ${descriptionBase}`
          : `SLGS Note interest — ${descriptionBase}`,
        // include principal redemption on final flow
        amount: isMaturity ? principal + interestAmt : interestAmt,
        includesPrincipal: isMaturity,
      });

      from = to;
    }

    return flows;
  };

  // Base schedule applying the 30-day minimum rule
  const baseStart = tooClose ? iNext + 1 : iNext;
  const regularSchedule = makeSchedule(baseStart);

  // Optional skip variant (one more 6-month deferral)
  let optionalSkip = regularSchedule;
  if (allowSkipFirstCoupon && baseStart + 1 < grid.length) {
    optionalSkip = makeSchedule(baseStart + 1);
  }

  const firstInterestPaymentDate =
    getFirstInterestPaymentDate(regularSchedule);

  const optionalSkipFirstInterestPaymentDate =
    getFirstInterestPaymentDate(optionalSkip);

  // Return both variants plus first-interest metadata
  return {
    regular: regularSchedule,
    optionalSkip,
    firstInterestPaymentDate,
    optionalSkipFirstInterestPaymentDate,
  };
}
